<template>
    <div>
        <div class="content-header row">
            <div class="content-header-left col-md-6 col-12 mb-2">
                <h3 class="content-header-title mb-0">Promo Report</h3>
                <div class="row breadcrumbs-top">
                    <div class="breadcrumb-wrapper col-12">
                        <ol class="breadcrumb">
                            <li class="breadcrumb-item"><Link href="/">Home</Link></li>
                            <li class="breadcrumb-item">Reports</li>
                            <li class="breadcrumb-item active">Promo Report</li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>

        <div class="content-body">
            <section class="card">
                <div class="card-header"><h4>Filters</h4></div>
                <div class="card-body">
                    <form @submit.prevent="applyFilters">
                        <div class="row">
                            <div class="col-xl-3 col-md-4 col-sm-6">
                                <label>Start date</label>
                                <date-time-field v-model="filter.start_date" class="form-control" bound="start"></date-time-field>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6">
                                <label>End date</label>
                                <date-time-field v-model="filter.end_date" class="form-control" bound="end"></date-time-field>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6">
                                <label>Store</label>
                                <select v-model="filter.store_id" class="form-control">
                                    <option value="">All stores</option>
                                    <option v-for="store in storeOptions" :key="store.id" :value="store.id">{{ store.name }}</option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6">
                                <label>Promo code or name</label>
                                <input v-model="filter.code" type="text" class="form-control" placeholder="Code or name">
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Promo type</label>
                                <select v-model="filter.promo_type" class="form-control">
                                    <option value="">All types</option>
                                    <option v-for="type in typeOptions" :key="type.value" :value="type.value">{{ type.label }}</option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Promo status</label>
                                <select v-model="filter.status" class="form-control">
                                    <option value="">All statuses</option>
                                    <option v-for="status in statusOptions" :key="status.value" :value="status.value">{{ status.label }}</option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>&nbsp;</label><br>
                                <button type="submit" class="btn btn-primary" :disabled="loading">Filter</button>
                                <button type="button" class="btn btn-default" @click="resetFilter">Reset</button>
                            </div>
                        </div>
                    </form>
                </div>
            </section>

            <section class="card">
                <div class="card-body">
                    <p v-if="report.date_label" class="text-center"><b>{{ report.date_label }}</b></p>
                    <p class="text-muted">Includes automatic and manual promos. Gift cards are excluded.</p>
                    <div class="row grouped-multiple-statistics-card">
                        <div v-for="metric in metricCards" :key="metric.label" class="col-lg-3 col-md-6 mb-2">
                            <h3 class="heading-text text-bold-600">{{ metric.value }}</h3>
                            <p class="sub-heading mb-0">{{ metric.label }}</p>
                        </div>
                    </div>
                </div>
            </section>

            <section class="card p-3">
                <h4 class="content-header-title mb-2">Promo orders by status</h4>
                <div class="row">
                    <div v-for="(count, status) in report.orders_by_status" :key="status" class="col-md-2 mb-1">
                        <strong>{{ count }}</strong>
                        <div class="text-capitalize">{{ status }}</div>
                    </div>
                </div>
            </section>

            <section class="card p-3">
                <div class="row">
                    <div class="col-md-7">
                        <h4 class="content-header-title mb-2">Top products with promos</h4>
                        <BarChart :chart-data="productBar" :chart-options="barOptions"/>
                    </div>
                    <div class="col-md-5">
                        <h4 class="content-header-title mb-2">Redeemed vs expired unused</h4>
                        <BarChart v-if="expiryBar" :chart-data="expiryBar" :chart-options="barOptions"/>
                    </div>
                </div>
            </section>

            <section class="card">
                <div class="card-header"><h4>Promos</h4></div>
                <div class="card-body">
                    <vue-good-table
                        class="dataTable"
                        mode="remote"
                        :isLoading.sync="loading"
                        :totalRows="(report.promos_meta && report.promos_meta.total) || 0"
                        :pagination-options="promoPagination"
                        :sort-options="{enabled: false}"
                        :search-options="{enabled: true, placeholder: 'Search promos'}"
                        @on-search="onPromosSearch"
                        @on-page-change="onPromosPageChange"
                        @on-per-page-change="onPromosPerPageChange"
                        styleClass="vgt-table"
                        :columns="promoColumns"
                        :rows="report.promos || []"
                    >
                        <div slot="emptystate" class="text-center py-2">
                            <img v-if="loading" src="/images/Spinner.svg" alt="">
                            <span v-else>No promos for the selected filters.</span>
                        </div>
                        <template slot="table-row" slot-scope="props">
                            <span v-if="props.column.field === 'discount_given'">{{ money(props.row.discount_given) }}</span>
                            <span v-else-if="props.column.field === 'net_revenue'">{{ money(props.row.net_revenue) }}</span>
                            <span v-else>{{ props.formattedRow[props.column.field] }}</span>
                        </template>
                    </vue-good-table>
                </div>
            </section>

            <section class="card">
                <div class="card-header"><h4>Orders placed with promos</h4></div>
                <div class="card-body">
                    <vue-good-table
                        class="dataTable"
                        mode="remote"
                        :isLoading.sync="loading"
                        :totalRows="(report.orders_meta && report.orders_meta.total) || 0"
                        :pagination-options="orderPagination"
                        :sort-options="{enabled: false}"
                        :search-options="{enabled: true, placeholder: 'Search orders'}"
                        @on-search="onOrdersSearch"
                        @on-page-change="onOrdersPageChange"
                        @on-per-page-change="onOrdersPerPageChange"
                        styleClass="vgt-table"
                        :columns="orderColumns"
                        :rows="report.orders || []"
                    >
                        <div slot="emptystate" class="text-center py-2">
                            <img v-if="loading" src="/images/Spinner.svg" alt="">
                            <span v-else>No promo orders for the selected filters.</span>
                        </div>
                        <template slot="table-row" slot-scope="props">
                            <span v-if="props.column.field === 'order_number'">
                                <a :href="route('admin.orders.show', props.row.id)">{{ props.row.order_number }}</a>
                            </span>
                            <span v-else-if="props.column.field === 'applied'">{{ props.row.apply_automatically ? 'Automatic' : 'Manual' }}</span>
                            <span v-else-if="props.column.field === 'discount'">{{ money(props.row.discount) }}</span>
                            <span v-else-if="props.column.field === 'revenue'">{{ money(props.row.revenue) }}</span>
                            <span v-else>{{ props.formattedRow[props.column.field] || '—' }}</span>
                        </template>
                    </vue-good-table>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import Layout from '@/Shared/Layout';
import {Link} from '@inertiajs/vue2';
import BarChart from '../../Shared/BarChart';

const emptyReport = () => ({
    date_label: '',
    metrics: {
        total_created: 0,
        active: 0,
        total_redeemed: 0,
        orders_placed: 0,
        discount_given: 0,
        revenue: 0,
        cancelled_orders: 0,
        redemptions: 0,
        expired: 0,
    },
    orders_by_status: {},
    products: [],
    promos: [],
    promos_meta: {current_page: 1, last_page: 1, per_page: 10, total: 0, from: 0, to: 0},
    orders: [],
    orders_meta: {current_page: 1, last_page: 1, per_page: 10, total: 0, from: 0, to: 0},
    filters: {stores: [], types: [], statuses: []},
});

export default {
    metaInfo: {title: 'Promo Report'},
    layout: Layout,
    name: 'PromoReport',
    components: {BarChart, Link},
    props: {
        date_label: {default: ''},
        metrics: {default: () => ({})},
        orders_by_status: {default: () => ({})},
        products: {default: () => []},
        promos: {default: () => []},
        promos_meta: {default: () => ({current_page: 1, last_page: 1, per_page: 10, total: 0, from: 0, to: 0})},
        orders: {default: () => []},
        orders_meta: {default: () => ({current_page: 1, last_page: 1, per_page: 10, total: 0, from: 0, to: 0})},
        filters: {default: () => ({stores: [], types: [], statuses: []})},
    },
    data() {
        return {
            loading: false,
            filter: {
                start_date: '',
                end_date: '',
                store_id: '',
                code: '',
                promo_type: '',
                status: '',
            },
            promosPage: 1,
            ordersPage: 1,
            promosSearch: '',
            ordersSearch: '',
            fetchTimer: null,
            promosPerPage: 10,
            ordersPerPage: 10,
            promoColumns: [
                {label: 'Promo name', field: 'name'},
                {label: 'Type', field: 'type'},
                {label: 'Redemptions', field: 'redemptions', type: 'number'},
                {label: 'Orders', field: 'order_count', type: 'number'},
                {label: 'Discount Given', field: 'discount_given', type: 'number'},
                {label: 'Net Revenue', field: 'net_revenue', type: 'number'},
                {label: 'Status', field: 'status'},
            ],
            orderColumns: [
                {label: 'Order', field: 'order_number'},
                {label: 'Date', field: 'date'},
                {label: 'Store', field: 'store'},
                {label: 'Customer', field: 'customer'},
                {label: 'Promo', field: 'code'},
                {label: 'Applied', field: 'applied'},
                {label: 'Status', field: 'status'},
                {label: 'Discount', field: 'discount', type: 'number'},
                {label: 'Revenue', field: 'revenue', type: 'number'},
            ],
            promoPagination: {
                enabled: true,
                mode: 'pages',
                perPage: 10,
                perPageDropdown: [5, 10, 20, 50, 100],
                setCurrentPage: 1,
            },
            orderPagination: {
                enabled: true,
                mode: 'pages',
                perPage: 10,
                perPageDropdown: [5, 10, 20, 50, 100],
                setCurrentPage: 1,
            },
            report: emptyReport(),
            productBar: {
                labels: [],
                datasets: [{label: 'Units sold', backgroundColor: '#00B5B8', data: []}],
            },
            expiryBar: null,
            barOptions: {responsive: true, maintainAspectRatio: false},
        };
    },
    computed: {
        storeOptions() {
            return this.report.filters?.stores || [];
        },
        typeOptions() {
            return this.report.filters?.types || [];
        },
        statusOptions() {
            return this.report.filters?.statuses || [];
        },
        metricCards() {
            const metrics = this.report.metrics || {};
            return [
                {label: 'Total promos created', value: metrics.total_created || 0},
                {label: 'Active promos', value: metrics.active || 0},
                {label: 'Promos redeemed', value: metrics.promos_redeemed || 0},
                {label: 'Orders using promos', value: metrics.orders_placed || 0},
                {label: 'Discount given', value: this.money(metrics.discount_given)},
                {label: 'Revenue from promo orders', value: this.money(metrics.revenue)},
                {label: 'Cancelled promo orders', value: metrics.cancelled_orders || 0},
                {label: 'Expired unused', value: metrics.expired || 0},
            ];
        },
    },
    mounted() {
        this.applyReport(this.$props);
    },
    methods: {
        applyReport(data) {
            this.report = {
                date_label: data.date_label,
                metrics: data.metrics,
                orders_by_status: data.orders_by_status,
                products: data.products,
                promos: data.promos,
                promos_meta: data.promos_meta,
                orders: data.orders,
                orders_meta: data.orders_meta,
                filters: data.filters,
            };
            this.productBar = this.toBar(data.products || []);
            this.promoPagination = {
                ...this.promoPagination,
                perPage: data.promos_meta?.per_page || this.promosPerPage,
                setCurrentPage: data.promos_meta?.current_page || 1,
            };
            this.orderPagination = {
                ...this.orderPagination,
                perPage: data.orders_meta?.per_page || this.ordersPerPage,
                setCurrentPage: data.orders_meta?.current_page || 1,
            };
            this.expiryBar = {
                labels: ['Redeemed', 'Expired unused'],
                datasets: [{
                    label: 'Promos',
                    backgroundColor: ['#00B5B8', '#EF5350'],
                    data: [data.metrics?.promos_redeemed || 0, data.metrics?.expired || 0],
                }],
            };
        },
        async fetchReport() {
            clearTimeout(this.fetchTimer);
            this.loading = true;
            try {
                const response = await axios.get(route('admin.reports.promos'), {
                    params: {
                        ...this.filter,
                        api: true,
                        promos_search: this.promosSearch,
                        orders_search: this.ordersSearch,
                        promos_per_page: this.promosPerPage,
                        orders_per_page: this.ordersPerPage,
                        promos_page: this.promosPage,
                        orders_page: this.ordersPage,
                    },
                    headers: {'X-Requested-With': 'XMLHttpRequest'},
                });
                this.applyReport(response.data);
            } catch (error) {
                const message = error.response?.data?.message
                    || error.response?.data?.errors?.start_date?.[0]
                    || 'Unable to load the promo report.';
                this.$store.commit('notifier/showNotifier', {message, type: 'danger'});
            } finally {
                this.loading = false;
            }
        },
        applyFilters() {
            this.promosPage = 1;
            this.ordersPage = 1;
            this.fetchReport();
        },
        onPromosSearch(params) {
            const term = (params.searchTerm || '').trim();
            if (term === this.promosSearch) return;
            this.promosSearch = term;
            this.promosPage = 1;
            this.scheduleFetch(300);
        },
        onOrdersSearch(params) {
            const term = (params.searchTerm || '').trim();
            if (term === this.ordersSearch) return;
            this.ordersSearch = term;
            this.ordersPage = 1;
            this.scheduleFetch(300);
        },
        scheduleFetch(delay) {
            clearTimeout(this.fetchTimer);
            this.fetchTimer = setTimeout(() => this.fetchReport(), delay);
        },
        onPromosPageChange(params) {
            if (params.currentPage === this.promosPage) return;
            this.promosPage = params.currentPage;
            this.scheduleFetch(0);
        },
        onPromosPerPageChange(params) {
            if (params.currentPerPage === this.promosPerPage) return;
            this.promosPerPage = params.currentPerPage;
            this.promosPage = 1;
            this.fetchReport();
        },
        onOrdersPageChange(params) {
            if (params.currentPage === this.ordersPage) return;
            this.ordersPage = params.currentPage;
            this.scheduleFetch(0);
        },
        onOrdersPerPageChange(params) {
            if (params.currentPerPage === this.ordersPerPage) return;
            this.ordersPerPage = params.currentPerPage;
            this.ordersPage = 1;
            this.fetchReport();
        },
        resetFilter() {
            Object.keys(this.filter).forEach(key => { this.filter[key] = ''; });
            this.applyFilters();
        },
        toBar(products) {
            return {
                labels: products.map(product => product.name),
                datasets: [{
                    label: 'Units sold',
                    backgroundColor: '#00B5B8',
                    data: products.map(product => product.units),
                }],
            };
        },
        money(value) {
            return `₦${Number(value || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
        },
    },
};
</script>
